import { useState, useCallback } from "react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

// ─── DESIGN TOKENS (mirrors App.js) ──────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;700;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --green: #39FF14; --green-dim: #2aaa10; --green-glow: rgba(57,255,20,0.18);
    --green-faint: rgba(57,255,20,0.06); --black: #000000; --bg: #070a06;
    --bg-card: #0b0f0a; --bg-card2: #0f1410;
    --border: rgba(57,255,20,0.15); --border-bright: rgba(57,255,20,0.35);
    --text: #e8f5e3; --text-muted: #7aaa6a; --text-dim: #6aaa55;
    --red: #ff3b3b; --amber: #ffb800;
    --mono: 'Share Tech Mono', monospace; --display: 'Orbitron', sans-serif;
    --body: 'Rajdhani', sans-serif;
    --glow: 0 0 12px rgba(57,255,20,0.3);
    --glow-strong: 0 0 24px rgba(57,255,20,0.5), 0 0 48px rgba(57,255,20,0.2);
  }
  html, body, #root { height: 100%; background: var(--black); color: var(--text); font-family: var(--body); font-size: 15px; line-height: 1.5; -webkit-font-smoothing: antialiased; }
  body::before { content: ''; position: fixed; inset: 0; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px); pointer-events: none; z-index: 9999; }
  ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: var(--black); } ::-webkit-scrollbar-thumb { background: var(--green-dim); border-radius: 2px; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes pulse-green { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes flicker { 0%,100% { opacity:1 } 92% { opacity:1 } 93% { opacity:0.4 } 94% { opacity:1 } }
`;

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function formatDate(val) {
  if (!val) return "—";
  const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
  return d.toLocaleString();
}

function Card({ children, style }) {
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 2, padding: "18px 20px", ...style }}>
      {children}
    </div>
  );
}

function Tag({ children, color }) {
  const c = color || "var(--green)";
  return (
    <span style={{ fontFamily: "var(--mono)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: c, border: `1px solid ${c}`, padding: "2px 7px", borderRadius: 1, background: `${c}11` }}>
      {children}
    </span>
  );
}

function Spinner() {
  return <div style={{ width: 14, height: 14, border: "2px solid var(--green-dim)", borderTopColor: "var(--green)", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />;
}

// ─── LOGIN SCREEN ──────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit() {
    if (!token.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/feedback?limit=1`, {
        headers: { Authorization: `Bearer ${token.trim()}` },
      });
      if (res.status === 401) throw new Error("Invalid admin token.");
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      onLogin(token.trim());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 380, animation: "fadeUp 0.4s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 900, color: "var(--green)", letterSpacing: "0.15em", textShadow: "var(--glow)", animation: "flicker 12s ease infinite", marginBottom: 6 }}>
            NODE // REVEAL
          </div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
            Admin Feedback Review
          </div>
        </div>

        <Card style={{ border: "1px solid var(--border-bright)" }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.2em", marginBottom: 16 }}>
            ▶ Authentication Required
          </div>

          <label style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>
            Admin Token
          </label>
          <input
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            placeholder="Enter ADMIN_TOKEN from .env"
            style={{ width: "100%", background: "var(--black)", border: "1px solid var(--border)", borderRadius: 1, padding: "9px 12px", color: "var(--text)", fontFamily: "var(--mono)", fontSize: 12, outline: "none", marginBottom: 12, letterSpacing: "0.1em" }}
            onFocus={e => e.target.style.borderColor = "var(--green-dim)"}
            onBlur={e => e.target.style.borderColor = "var(--border)"}
          />

          {error && (
            <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--red)", marginBottom: 12 }}>✗ {error}</div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !token.trim()}
            style={{ width: "100%", padding: "11px 0", background: "var(--green-faint)", border: "1px solid var(--green-dim)", borderRadius: 1, color: "var(--green)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.12em", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: !token.trim() ? 0.5 : 1 }}
          >
            {loading ? <><Spinner /> AUTHENTICATING...</> : "ACCESS PANEL"}
          </button>
        </Card>
      </div>
    </div>
  );
}

// ─── FEEDBACK CARD ─────────────────────────────────────────────────────────────

function FeedbackCard({ entry, token, onReviewed }) {
  const [loading, setLoading] = useState(null); // 'approve' | 'reject' | null
  const [reviewNote, setReviewNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  async function submitReview(action) {
    setLoading(action);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/feedback/${entry.imageHash}/${entry.id}/review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action, reviewNote: reviewNote || null }),
        }
      );
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      onReviewed(entry.id, action === "approve" ? "approved" : "rejected");
    } catch (e) {
      alert(`Failed to ${action}: ${e.message}`);
    } finally {
      setLoading(null);
      setPendingAction(null);
    }
  }

  function handleAction(action) {
    setPendingAction(action);
    setShowNoteInput(true);
  }

  const confidencePct = entry.originalConfidence != null
    ? Math.round(entry.originalConfidence * 100)
    : null;
  const confColor = confidencePct >= 76 ? "var(--green)" : confidencePct >= 50 ? "var(--amber)" : "var(--red)";

  return (
    <Card style={{ animation: "fadeUp 0.3s ease", position: "relative", overflow: "hidden" }}>
      {/* Status accent bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, var(--green-dim), transparent)" }} />

      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 12 }}>
        <div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 4 }}>
            Image Hash
          </div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text-muted)", wordBreak: "break-all" }}>
            {entry.imageHash}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <Tag color="var(--amber)">PENDING REVIEW</Tag>
          <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", marginTop: 6 }}>
            {formatDate(entry.createdAt)}
          </div>
        </div>
      </div>

      {/* Original AI result */}
      <div style={{ background: "var(--bg-card2)", border: "1px solid var(--border)", borderRadius: 1, padding: "12px 14px", marginBottom: 14 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 10 }}>
          ▶ Original AI Analysis
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px" }}>
          {[
            ["Make", entry.originalMake],
            ["Model", entry.originalModel],
            ["Year", entry.originalYearRange],
            ["Color", entry.originalColor],
            ["Trim", entry.originalTrim],
          ].map(([label, val]) => val ? (
            <div key={label} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", flexShrink: 0 }}>{label}:</span>
              <span style={{ fontFamily: "var(--body)", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{val}</span>
            </div>
          ) : null)}
          {confidencePct != null && (
            <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase" }}>Conf:</span>
              <span style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, color: confColor, textShadow: `0 0 8px ${confColor}` }}>{confidencePct}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Analyst note */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 8 }}>
          ▶ Analyst Note
        </div>
        {entry.notes ? (
          <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7, background: "rgba(57,255,20,0.04)", border: "1px solid rgba(57,255,20,0.1)", borderRadius: 1, padding: "10px 12px" }}>
            "{entry.notes}"
          </div>
        ) : (
          <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text-dim)", fontStyle: "italic" }}>
            No note provided — user confirmed result was incorrect but left no details.
          </div>
        )}
      </div>

      {/* Review note input */}
      {showNoteInput && (
        <div style={{ marginBottom: 14, animation: "fadeUp 0.2s ease" }}>
          <label style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>
            Admin Review Note (optional)
          </label>
          <textarea
            value={reviewNote}
            onChange={e => setReviewNote(e.target.value)}
            placeholder={pendingAction === "approve" ? "e.g. Verified against reference image" : "e.g. Note seems incorrect / spam submission"}
            style={{ width: "100%", background: "var(--black)", border: "1px solid var(--border)", borderRadius: 1, padding: "8px 10px", color: "var(--text)", fontFamily: "var(--mono)", fontSize: 11, resize: "vertical", minHeight: 56, outline: "none" }}
            onFocus={e => e.target.style.borderColor = "var(--green-dim)"}
            onBlur={e => e.target.style.borderColor = "var(--border)"}
          />
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 10 }}>
        {!showNoteInput ? (
          <>
            <button
              onClick={() => handleAction("approve")}
              style={{ flex: 1, padding: "10px 0", background: "var(--green-faint)", border: "1px solid var(--green-dim)", borderRadius: 1, color: "var(--green)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={e => e.target.style.background = "rgba(57,255,20,0.12)"}
              onMouseLeave={e => e.target.style.background = "var(--green-faint)"}
            >
              ✓ APPROVE
            </button>
            <button
              onClick={() => handleAction("reject")}
              style={{ flex: 1, padding: "10px 0", background: "rgba(255,59,59,0.06)", border: "1px solid rgba(255,59,59,0.3)", borderRadius: 1, color: "var(--red)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={e => e.target.style.background = "rgba(255,59,59,0.12)"}
              onMouseLeave={e => e.target.style.background = "rgba(255,59,59,0.06)"}
            >
              ✗ REJECT
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => { setShowNoteInput(false); setPendingAction(null); setReviewNote(""); }}
              style={{ padding: "10px 16px", background: "transparent", border: "1px solid var(--border)", borderRadius: 1, color: "var(--text-muted)", fontFamily: "var(--mono)", fontSize: 11, cursor: "pointer", letterSpacing: "0.1em" }}
            >
              CANCEL
            </button>
            <button
              onClick={() => submitReview(pendingAction)}
              disabled={!!loading}
              style={{ flex: 1, padding: "10px 0", background: pendingAction === "approve" ? "var(--green-faint)" : "rgba(255,59,59,0.06)", border: `1px solid ${pendingAction === "approve" ? "var(--green-dim)" : "rgba(255,59,59,0.3)"}`, borderRadius: 1, color: pendingAction === "approve" ? "var(--green)" : "var(--red)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {loading ? <><Spinner /> SUBMITTING...</> : `CONFIRM ${pendingAction?.toUpperCase()}`}
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

// ─── MAIN PANEL ────────────────────────────────────────────────────────────────

export default function AdminPanel() {
  const [token, setToken] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [activeTab, setActiveTab] = useState("analysis"); // analysis | general | users
  const [counts, setCounts] = useState({ pending: "?", approved: "?", rejected: "?", general: "?" });
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [subModal, setSubModal] = useState(null); // { clerkUserId, email }
  const [subTier, setSubTier] = useState('monthly');
  const [subDays, setSubDays] = useState('30');
  const [subNote, setSubNote] = useState('');
  const [subSaving, setSubSaving] = useState(false);

  const fetchEntries = useCallback(async (status, tok, tab) => {
    setLoading(true);
    setError(null);
    try {
      const isGeneral = (tab || activeTab) === "general";
      const url = isGeneral
        ? `${API_BASE_URL}/admin/general-feedback?limit=100`
        : `${API_BASE_URL}/admin/feedback?status=${status}&limit=100`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok || token}` } });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  const fetchCounts = useCallback(async (tok) => {
    const t = tok || token;
    try {
      const [p, a, r, g] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/feedback?status=pending&limit=1000`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()),
        fetch(`${API_BASE_URL}/admin/feedback?status=approved&limit=1000`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()),
        fetch(`${API_BASE_URL}/admin/feedback?status=rejected&limit=1000`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()),
        fetch(`${API_BASE_URL}/admin/general-feedback?limit=1000`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()),
      ]);
      setCounts({ pending: p.count ?? "?", approved: a.count ?? "?", rejected: r.count ?? "?", general: g.count ?? "?" });
    } catch {}
  }, [token]);

  function handleLogin(tok) {
    setToken(tok);
    fetchEntries("pending", tok, "analysis");
    fetchCounts(tok);
  }

  function handleReviewed(submissionId, newStatus) {
    setEntries(prev => prev.filter(e => e.id !== submissionId));
    setCounts(prev => ({
      ...prev,
      pending: Math.max(0, (prev.pending || 1) - 1),
      [newStatus]: (prev[newStatus] || 0) + 1,
    }));
  }

  const fetchUsers = useCallback(async (tok) => {
    setUsersLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users?limit=200`, {
        headers: { Authorization: `Bearer ${tok || token}` },
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  async function handleGrantSubscription() {
    if (!subModal || !subDays) return;
    setSubSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${subModal.clerkUserId}/subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier: subTier, durationDays: parseInt(subDays), note: subNote }),
      });
      if (!res.ok) throw new Error('Failed to grant subscription');
      await fetchUsers(token);
      setSubModal(null);
      setSubTier('monthly');
      setSubDays('30');
      setSubNote('');
    } catch (e) {
      alert(e.message);
    } finally {
      setSubSaving(false);
    }
  }

  function switchTab(tab) {
    setActiveTab(tab);
    setEntries([]);
    if (tab === "general") {
      fetchEntries("pending", token, "general");
    } else if (tab === "users") {
      fetchUsers(token);
    } else {
      fetchEntries(statusFilter, token, "analysis");
    }
  }

  function switchFilter(status) {
    setStatusFilter(status);
    fetchEntries(status, token, "analysis");
  }

  if (!token) return (
    <>
      <style>{css}</style>
      <LoginScreen onLogin={handleLogin} />
    </>
  );

  const filterLabels = [
    { key: "pending", label: "PENDING", count: counts.pending, color: "var(--amber)" },
    { key: "approved", label: "APPROVED", count: counts.approved, color: "var(--green)" },
    { key: "rejected", label: "REJECTED", count: counts.rejected, color: "var(--red)" },
  ];

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <header style={{ borderBottom: "1px solid var(--border)", padding: "0 28px", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--black)", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 40, height: 40, border: "1px solid var(--green-dim)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--green-faint)" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ filter: "drop-shadow(0 0 4px #39FF14)" }}>
                <path d="M9 12l2 2 4-4M12 3a9 9 0 100 18A9 9 0 0012 3z" stroke="#39FF14" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 900, color: "var(--green)", letterSpacing: "0.15em", textShadow: "var(--glow)", animation: "flicker 12s ease infinite" }}>
                NODE // REVEAL
              </div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
                Admin · Feedback Review
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            {/* Live counts */}
            {[
              { key: "pending", label: "PENDING", count: counts.pending, color: "var(--amber)" },
              { key: "approved", label: "APPROVED", count: counts.approved, color: "var(--green)" },
              { key: "rejected", label: "REJECTED", count: counts.rejected, color: "var(--red)" },
              { key: "general", label: "APP FEEDBACK", count: counts.general, color: "#4f6ef7" },
            ].map(f => (
              <div key={f.key} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--display)", fontSize: 16, fontWeight: 700, color: f.color, textShadow: `0 0 10px ${f.color}` }}>{f.count}</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 8, color: "var(--text-dim)", letterSpacing: "0.15em" }}>{f.label}</div>
              </div>
            ))}
            <div style={{ width: 1, height: 30, background: "var(--border)" }} />
            <button
              onClick={() => { setToken(null); setEntries([]); }}
              style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", background: "transparent", border: "1px solid var(--border)", borderRadius: 1, padding: "5px 12px", cursor: "pointer", letterSpacing: "0.1em" }}
            >
              LOGOUT
            </button>
          </div>
        </header>

        {/* Top-level tab selector */}
        <div style={{ borderBottom: "1px solid var(--border)", background: "var(--black)", padding: "0 28px", display: "flex", gap: 0 }}>
          {[
            { key: "analysis", label: "ANALYSIS FEEDBACK" },
            { key: "general", label: "APP FEEDBACK", count: counts.general },
            { key: "users", label: "USERS" },
          ].map(t => (
            <button key={t.key} onClick={() => switchTab(t.key)}
              style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.12em", padding: "12px 18px", background: "transparent", border: "none", borderBottom: `2px solid ${activeTab === t.key ? "var(--green)" : "transparent"}`, color: activeTab === t.key ? "var(--green)" : "var(--text-dim)", cursor: "pointer", transition: "all 0.2s" }}
            >
              {t.label}{t.count !== undefined ? ` (${t.count})` : ""}
            </button>
          ))}
        </div>

        {/* Analysis feedback sub-filters */}
        {activeTab === "analysis" && (
        <div style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-card2)", padding: "0 28px", display: "flex", gap: 4 }}>
          {filterLabels.map(f => (
            <button key={f.key} onClick={() => switchFilter(f.key)}
              style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.12em", padding: "9px 14px", background: "transparent", border: "none", borderBottom: `2px solid ${statusFilter === f.key ? f.color : "transparent"}`, color: statusFilter === f.key ? f.color : "var(--text-dim)", cursor: "pointer", transition: "all 0.2s" }}
            >
              {f.label} ({f.count})
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={() => fetchEntries(statusFilter, token, "analysis")}
            style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", background: "transparent", border: "none", cursor: "pointer", padding: "9px 0", letterSpacing: "0.1em" }}
          >↺ REFRESH</button>
        </div>
        )}

        {/* Content */}
        <main style={{ flex: 1, maxWidth: 1100, width: "100%", margin: "0 auto", padding: "28px 20px" }}>

          {/* ── USERS TAB ── */}
          {activeTab === "users" && (
            <div>
              {/* Grant Subscription Modal */}
              {subModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(4px)' }}>
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 2, padding: '28px 32px', width: 400, position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />
                    <div style={{ fontFamily: 'var(--display)', fontSize: 13, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.15em', marginBottom: 6 }}>GRANT SUBSCRIPTION</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', marginBottom: 20 }}>{subModal.email}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {['monthly', 'annual'].map(t => (
                          <button key={t} onClick={() => setSubTier(t)}
                            style={{ flex: 1, padding: '9px 0', background: subTier === t ? 'var(--green-faint)' : 'transparent', border: `1px solid ${subTier === t ? 'var(--green-dim)' : 'var(--border)'}`, borderRadius: 1, color: subTier === t ? 'var(--green)' : 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                            {t}
                          </button>
                        ))}
                      </div>
                      <input type="number" min="1" max="365" placeholder="DURATION (DAYS)"
                        value={subDays} onChange={e => setSubDays(e.target.value)}
                        style={{ width: '100%', background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 1, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }}
                      />
                      <input type="text" placeholder="NOTE (optional)"
                        value={subNote} onChange={e => setSubNote(e.target.value)}
                        style={{ width: '100%', background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 1, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }}
                      />
                      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <button onClick={() => { setSubModal(null); setSubDays('30'); setSubNote(''); }}
                          style={{ padding: '9px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer' }}>CANCEL</button>
                        <button onClick={handleGrantSubscription} disabled={!subDays || subSaving}
                          style={{ flex: 1, padding: '9px 0', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>
                          {subSaving ? 'SAVING...' : 'GRANT ACCESS'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.1em' }}>{users.length} REGISTERED USER{users.length !== 1 ? 'S' : ''}</div>
                <button onClick={() => fetchUsers(token)} style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', background: 'transparent', border: 'none', cursor: 'pointer' }}>↺ REFRESH</button>
              </div>

              {usersLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '40px 0', justifyContent: 'center' }}><Spinner /><span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>LOADING USERS...</span></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1fr 1fr 1fr auto', gap: 12, padding: '8px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.12em' }}>
                    <span>EMAIL</span><span>ROLE</span><span>SUBSCRIPTION</span><span>ANALYSES</span><span>THIS MONTH</span><span>JOINED</span><span></span>
                  </div>
                  {users.map(u => {
                    const isActive = u.subscription_tier && u.subscription_tier !== 'free'
                      && u.subscription_expires_at && new Date(u.subscription_expires_at) > new Date();
                    const expiry = u.subscription_expires_at ? new Date(u.subscription_expires_at).toLocaleDateString() : null;
                    return (
                      <Card key={u.clerk_user_id} style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'center' }}>
                          <div>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text)' }}>{u.email}</div>
                            {u.display_name && u.display_name !== u.email?.split('@')[0] && (
                              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>{u.display_name}</div>
                            )}
                          </div>
                          <Tag color={u.role === 'admin' ? 'var(--amber)' : 'var(--green)'}>{u.role || 'user'}</Tag>
                          <div>
                            <Tag color={isActive ? 'var(--green)' : 'var(--text-dim)'}>{isActive ? u.subscription_tier.toUpperCase() : 'FREE'}</Tag>
                            {isActive && expiry && <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--text-dim)', marginTop: 3 }}>until {expiry}</div>}
                            {!isActive && <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>{u.total_credits ?? 0} cr left</div>}
                          </div>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>{u.total_analyses ?? 0}</div>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>{u.this_month_usage ?? 0}</div>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</div>
                          <button onClick={() => setSubModal({ clerkUserId: u.clerk_user_id, email: u.email })}
                            style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                            ↑ SUB
                          </button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── FEEDBACK TABS ── */}
          {activeTab !== "users" && (
          <div>
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "40px 0", justifyContent: "center" }}>
              <Spinner />
              <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text-muted)" }}>LOADING...</span>
            </div>
          )}

          {error && (
            <Card style={{ borderColor: "rgba(255,59,59,0.4)", background: "rgba(255,59,59,0.05)" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--red)" }}>✗ ERROR: {error}</span>
            </Card>
          )}

          {!loading && !error && entries.length === 0 && (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 28, color: "var(--green-dim)", marginBottom: 16 }}>✓</div>
              <div style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: "var(--text-dim)", letterSpacing: "0.15em" }}>
                NO {activeTab === "general" ? "APP FEEDBACK" : statusFilter.toUpperCase()} SUBMISSIONS
              </div>
            </div>
          )}

          {!loading && entries.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", letterSpacing: "0.1em", paddingBottom: 8 }}>
                {entries.length} {activeTab === "general" ? "app feedback" : statusFilter} submission{entries.length !== 1 ? "s" : ""}
              </div>
              {activeTab === "general"
                ? entries.map(entry => <GeneralFeedbackEntry key={entry.id} entry={entry} />)
                : entries.map(entry => (
                    statusFilter === "pending"
                      ? <FeedbackCard key={entry.id} entry={entry} token={token} onReviewed={handleReviewed} />
                      : <ReviewedCard key={entry.id} entry={entry} />
                  ))
              }
            </div>
          )}
          </div>
          )}
        </main>

        {/* Footer */}
        <footer style={{ borderTop: "1px solid var(--border)", padding: "12px 28px", display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", letterSpacing: "0.15em" }}>© 2025 OPERATION // NODE INC.</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", letterSpacing: "0.1em" }}>ADMIN PANEL — RESTRICTED ACCESS</span>
        </footer>
      </div>
    </>
  );
}

// ─── GENERAL FEEDBACK ENTRY (read-only display card) ─────────────────────────

const CATEGORY_COLORS = {
  bug: "var(--red)",
  accuracy: "var(--amber)",
  feature: "#4f6ef7",
  ui: "#a855f7",
  general: "var(--green)",
};

function GeneralFeedbackEntry({ entry }) {
  const color = CATEGORY_COLORS[entry.category] || "var(--green)";
  return (
    <Card style={{ animation: "fadeUp 0.3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Tag color={color}>{entry.category || "general"}</Tag>
          {entry.name && (
            <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-muted)" }}>{entry.name}</span>
          )}
          {entry.email && (
            <a href={`mailto:${entry.email}`} style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--green-dim)", textDecoration: "none" }}>{entry.email}</a>
          )}
        </div>
        <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", flexShrink: 0 }}>{formatDate(entry.createdAt)}</span>
      </div>
      <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.8, background: "rgba(57,255,20,0.03)", border: "1px solid var(--border)", borderRadius: 1, padding: "12px 14px" }}>
        {entry.message}
      </div>
    </Card>
  );
}

function ReviewedCard({ entry }) {
  const isApproved = entry.status === "approved";
  const accentColor = isApproved ? "var(--green)" : "var(--red)";
  const accentFaint = isApproved ? "var(--green-faint)" : "rgba(255,59,59,0.06)";
  const confidencePct = entry.originalConfidence != null ? Math.round(entry.originalConfidence * 100) : null;
  const confColor = confidencePct >= 76 ? "var(--green)" : confidencePct >= 50 ? "var(--amber)" : "var(--red)";

  return (
    <Card style={{ opacity: 0.75 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", wordBreak: "break-all" }}>
          {entry.imageHash}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
          <Tag color={accentColor}>{entry.status.toUpperCase()}</Tag>
          <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)" }}>{formatDate(entry.reviewedAt)}</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: entry.notes ? 12 : 0 }}>
        {[
          ["Make", entry.originalMake], ["Model", entry.originalModel],
          ["Year", entry.originalYearRange], ["Color", entry.originalColor],
        ].map(([l, v]) => v ? (
          <div key={l} style={{ display: "flex", gap: 6 }}>
            <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase" }}>{l}:</span>
            <span style={{ fontFamily: "var(--body)", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{v}</span>
          </div>
        ) : null)}
        {confidencePct != null && (
          <div style={{ display: "flex", gap: 6 }}>
            <span style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase" }}>Conf:</span>
            <span style={{ fontFamily: "var(--mono)", fontSize: 12, fontWeight: 700, color: confColor }}>{confidencePct}%</span>
          </div>
        )}
      </div>

      {entry.notes && (
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text-muted)", background: accentFaint, border: `1px solid ${accentColor}22`, borderRadius: 1, padding: "8px 12px", marginBottom: entry.reviewNote ? 8 : 0 }}>
          "{entry.notes}"
        </div>
      )}
      {entry.reviewNote && (
        <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", marginTop: 6 }}>
          Admin note: {entry.reviewNote}
        </div>
      )}
    </Card>
  );
}